import { useEffect, useRef, useState } from "react";
import { ActionIcon, Anchor, Button, Group, Stack, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCamera, IconPaperclip, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { ShipmentAttachment } from "../../api/types";
import { DocumentCameraModal } from "../DocumentCameraModal";

export function ShipmentDocuments({ shipmentId, canEdit }: { shipmentId: number; canEdit: boolean }) {
  const { t } = useTranslation();
  const [attachments, setAttachments] = useState<ShipmentAttachment[]>([]);
  const [note, setNote] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function load() {
    try {
      setAttachments(await api.listShipmentAttachments(shipmentId));
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId]);

  async function upload(file: File) {
    try {
      await api.uploadShipmentAttachment(shipmentId, file, note.trim() || undefined);
      setNote("");
      await load();
    } catch (e) {
      const tooLarge = e instanceof ApiError && (e.detail as { error?: string } | null)?.error === "file_too_large";
      notifications.show({
        color: "red",
        message: tooLarge ? t("shipments.docs.tooLarge") : e instanceof ApiError ? e.message : String(e),
      });
    }
  }

  async function remove(attachmentId: number) {
    if (!confirm(t("shipments.docs.confirmDelete"))) return;
    try {
      await api.deleteShipmentAttachment(shipmentId, attachmentId);
      await load();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  return (
    <Stack gap="sm">
      {canEdit && (
        <Group gap="sm" align="end" wrap="wrap">
          <TextInput
            label={t("shipments.docs.noteLabel")}
            placeholder={t("shipments.docs.notePlaceholder")}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            style={{ flex: "1 1 240px" }}
          />
          <Button variant="default" leftSection={<IconPaperclip size={16} />} onClick={() => fileInputRef.current?.click()}>
            {t("shipments.docs.file")}
          </Button>
          <Button variant="default" leftSection={<IconCamera size={16} />} onClick={() => setCameraOpen(true)}>
            {t("shipments.docs.camera")}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={(e) => {
              const f = e.currentTarget.files?.[0];
              if (f) void upload(f);
              e.currentTarget.value = "";
            }}
          />
        </Group>
      )}
      {attachments.length === 0 ? (
        <Text c="dimmed" size="sm">{t("shipments.docs.empty")}</Text>
      ) : (
        <Stack gap={4}>
          {attachments.map((a) => (
            <Group key={a.id} justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                <Anchor
                  href={api.shipmentAttachmentDownloadUrl(shipmentId, a.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  truncate
                >
                  {a.original_filename}
                </Anchor>
                {a.note && <Text size="xs" c="dimmed" truncate>— {a.note}</Text>}
              </Group>
              {canEdit && (
                <ActionIcon variant="subtle" color="red" onClick={() => void remove(a.id)} title={t("shipments.docs.delete")}>
                  <IconTrash size={14} />
                </ActionIcon>
              )}
            </Group>
          ))}
        </Stack>
      )}
      {canEdit && (
        <DocumentCameraModal opened={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={(f) => void upload(f)} />
      )}
    </Stack>
  );
}
